import { useState } from 'react';

import { useGetFormalOffer } from '@api/lendingAPI/lapiHooks';
import { FormattedMessage } from 'react-intl';

import { ConfirmedModal } from './ConfirmedModal';
import { InterstitialOffer } from './InterstitialOffer';
import { RejectionModal } from './RejectionModal';
import { useCreditCardWaitlist } from './useCreditCardWaitlist';

type CreditCardWaitlistInterstitialProps = {
    offerId: string;
    amount: number;
    goToHome: () => void;
    onLeaveOffer?: () => void;
};

type ViewState = 'offer' | 'confirmed' | 'rejection';

export const CreditCardWaitlistInterstitial = ({
    offerId,
    amount,
    goToHome,
    onLeaveOffer
}: CreditCardWaitlistInterstitialProps) => {
    const [view, setView] = useState<ViewState>('offer');
    const { formalOffer } = useGetFormalOffer(offerId);
    const { mutate } = useCreditCardWaitlist();

    const formalOfferId = formalOffer?.formal_offer_id;

    if (!formalOfferId) {
        return null;
    }

    const handleAccept = () => {
        mutate({
            response: 'accepted',
            offer_id: formalOfferId,
            drawdown_amount: String(amount)
        });
        setView('confirmed');
        onLeaveOffer?.();
    };

    const handleReject = () => {
        setView('rejection');
        onLeaveOffer?.();
    };

    const handleDismiss = () => {
        mutate({
            response: 'dismissed',
            offer_id: formalOfferId,
            drawdown_amount: String(amount)
        });
        goToHome();
    };

    const handleBack = () => {
        setView('offer');
    };

    const handleSubmitRejection = (reason?: string, detail?: string) => {
        mutate({
            response: 'rejected',
            offer_id: formalOfferId,
            drawdown_amount: String(amount),
            rejection_reason: reason as
                | 'already_have_business_card'
                | 'dont_need_credit_card'
                | 'benefits_not_compelling'
                | 'prefer_flexi_loan'
                | 'other'
                | undefined,
            rejection_reason_detail: detail
        });
        goToHome();
    };

    const handleSkipRejection = () => {
        mutate({
            response: 'rejected',
            offer_id: formalOfferId,
            drawdown_amount: String(amount)
        });
        goToHome();
    };

    return (
        <div className="bg-white">
            {view === 'offer' && (
                <>
                    <InterstitialOffer
                        onAccept={handleAccept}
                        onReject={handleReject}
                    />
                    <button
                        onClick={handleDismiss}
                        className="hover:text-primary-30 mt-m block w-full text-center text-s text-primary-40 underline"
                    >
                        <FormattedMessage
                            id="_.credit_card_waitlist.dismiss_link"
                            defaultMessage="Continue to dashboard"
                        />
                    </button>
                </>
            )}
            <ConfirmedModal
                isOpen={view === 'confirmed'}
                onGoToDashboard={goToHome}
            />
            <RejectionModal
                isOpen={view === 'rejection'}
                onBack={handleBack}
                onSubmit={handleSubmitRejection}
                onSkip={handleSkipRejection}
            />
        </div>
    );
};
